// src/pages/admin/Orders.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLayoutEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase, storage } from '../../lib/supabase';
import { useTheme } from '../../context/ThemeContext';
import { formatCurrency, formatDate } from '../../lib/utils';

type OrderStatus = 'solicitado' | 'em_producao' | 'enviado' | 'concluido' | 'cancelado';

const technicalFieldLabels: Record<string, string> = {
  km: 'KM',
  transmission: 'Câmbio',
  system: 'Tipo de ECU',
  reading_mode: 'Modo de leitura',
  dtc: 'DTCs',
  performance: 'Performance',
  tool: 'Ferramentas',
};

function parseLegacyOrderNotes(notes?: string) {
  const values: Record<string, string> = {};
  if (!notes || !notes.includes('|')) return values;

  const fields: Record<string, string> = {
    KM: 'km',
    'Câmbio': 'transmission',
    Cambio: 'transmission',
    Modo: 'reading_mode',
    'Tipo ECU': 'system',
    Ferramentas: 'tool',
    Perf: 'performance',
    DTCs: 'dtc',
    Obs: 'notes',
  };

  notes.split('|').forEach((part) => {
    const separator = part.indexOf(':');
    if (separator < 0) return;
    const field = fields[part.slice(0, separator).trim()];
    if (field) values[field] = part.slice(separator + 1).trim();
  });
  return values;
}

type OrdersPageCache = {
  orders: any[];
  franchisees: { id: string; company_name: string; code: string; user_id?: string }[];
};

const ordersPageCache = new Map<string, OrdersPageCache>();
let ordersPageViewState = {
  filterStatus: 'all',
  filterFranchisee: 'all',
  visibleCount: 10,
};
let ordersPageScrollY = 0;

function ExpandableFieldValue({ value, color, buttonColor }: { value: string; color: string; buttonColor: string }) {
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const measure = () => {
      const content = contentRef.current;
      if (!content) return;
      setCanExpand(content.scrollHeight > content.clientHeight + 1);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [value]);

  return (
    <div>
      <div
        ref={contentRef}
        style={{
          color,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflow: expanded ? 'visible' : 'hidden',
          display: expanded ? 'block' : '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: expanded ? 'unset' : 5,
        }}
      >
        {value}
      </div>
      {canExpand && (
        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          style={{
            marginTop: 6,
            padding: 0,
            border: 'none',
            background: 'transparent',
            color: buttonColor,
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {expanded ? 'Ver menos' : 'Ver mais'}
        </button>
      )}
    </div>
  );
}

const statusOptions = [
  { value: 'solicitado', label: 'Recebido', color: '#d4d4d4' },
  { value: 'em_producao', label: 'Em andamento', color: '#c8c8c8' },
  { value: 'enviado', label: 'Enviado', color: '#a0a0a0' },
  { value: 'concluido', label: 'Concluído', color: '#b0b0b0' },
] as const;

function mapStatusForFlow(status: string): OrderStatus {
  if (status === 'concluido') return 'concluido';
  if (status === 'solicitado') return 'solicitado';
  if (status === 'enviado') return 'enviado';
  return 'em_producao';
}

export default function AdminOrders() {
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [searchParams, setSearchParams] = useSearchParams();

  const initialCache = ordersPageCache.get(ordersPageViewState.filterStatus);
  const [orders, setOrders] = useState<any[]>(() => initialCache?.orders || []);
  const [loading, setLoading] = useState(() => !initialCache);
  const [updating, setUpdating] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [paymentModalOrder, setPaymentModalOrder] = useState<any>(null);
  const [filterStatus, setFilterStatus] = useState<string>(() => ordersPageViewState.filterStatus);
  const [filterFranchisee, setFilterFranchisee] = useState<string>(() => ordersPageViewState.filterFranchisee);
  const [visibleCount, setVisibleCount] = useState(() => ordersPageViewState.visibleCount);
  const [franchisees, setFranchisees] = useState<{ id: string; company_name: string; code: string; user_id?: string }[]>(
    () => initialCache?.franchisees || []
  );
  const [error, setError] = useState<string>('');
  const [orderFiles, setOrderFiles] = useState<any[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState<string>('');
  const [deletingOrderId, setDeletingOrderId] = useState<string>('');
  const [fileError, setFileError] = useState<string>('');
  const [filesLoadError, setFilesLoadError] = useState<string>('');
  const [paymentForm, setPaymentForm] = useState({
    amount: '0.00',
    paymentMethod: 'PIX',
    dueDate: '',
    paymentStatus: 'pendente',
  });

  useLayoutEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';

    const restoreScroll = () => window.scrollTo({ top: ordersPageScrollY, behavior: 'auto' });
    restoreScroll();
    const animationFrame = window.requestAnimationFrame(restoreScroll);

    const rememberScroll = () => {
      ordersPageScrollY = window.scrollY;
    };
    window.addEventListener('scroll', rememberScroll, { passive: true });

    return () => {
      window.cancelAnimationFrame(animationFrame);
      ordersPageScrollY = window.scrollY;
      window.removeEventListener('scroll', rememberScroll);
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  const colors = {
    background: isDark ? '#0d0d0d' : '#f3f4f6',
    surface: isDark ? '#141414' : '#ffffff',
    text: isDark ? '#e5e5e5' : '#1a1a1a',
    textSecondary: isDark ? '#888888' : '#777777',
    border: isDark ? '#222222' : '#e5e7eb',
    accent: '#c8c8c8',
  };

  const detailFieldPriority = [
    'order_number',
    'status',
    'franchisee_id',
    'vehicle_plate',
    'model',
    'chassi',
    'year',
    'engine',
    'cv',
    'fuel',
    'km',
    'transmission',
    'system',
    'reading_mode',
    'dtc',
    'performance',
    'tool',
    'notes',
    'created_at',
    'updated_at',
    'id',
  ];

  function formatFieldLabel(field: string) {
    if (technicalFieldLabels[field]) return technicalFieldLabels[field];
    return field
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase());
  }

  function formatFieldValue(field: string, value: any) {
    if (value === null || value === undefined || value === '') return '—';
    if (field === 'created_at' || field === 'updated_at') return formatDate(String(value));
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  }

  function orderDetails(order: any) {
    const legacyValues = parseLegacyOrderNotes(order?.notes);
    const details = { ...order };
    Object.entries(legacyValues).forEach(([field, value]) => {
      if (field === 'notes' || details[field] === null || details[field] === undefined || details[field] === '') {
        details[field] = value;
      }
    });
    return details;
  }

  function csvValue(value: any) {
    const formatted = typeof value === 'object' && value !== null ? JSON.stringify(value) : formatFieldValue('', value);
    return `"${String(formatted).replace(/"/g, '""')}"`;
  }

  function htmlValue(value: any) {
    const formatted = typeof value === 'object' && value !== null ? JSON.stringify(value, null, 2) : formatFieldValue('', value);
    return String(formatted)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  const reportFields = useMemo(() => {
    const keys = new Set<string>();
    orders.forEach((order) => {
      Object.keys(order).forEach((field) => {
        if (field !== 'franchisee' && field !== 'flow_status' && field !== 'payment') keys.add(field);
      });
    });

    return [
      'franchisee_company',
      'franchisee_code',
      ...detailFieldPriority.filter((field) => keys.has(field)),
      ...Array.from(keys).filter((field) => !detailFieldPriority.includes(field)),
    ];
  }, [orders]);

  const paymentReportFields = ['payment_method', 'payment_amount', 'payment_status', 'payment_due_date'];

  const printExcludedFields = new Set([
    'updated_at', 'id', 'km', 'transmission', 'hw_number', 'sw_number', 'sw_upgrade',
    'spare_part', 'system', 'protocol', 'reading_mode', 'dtc', 'performance', 'tool',
    'total_amount', 'created_by', 'origin_screen_v2', 'origin_document_v2',
    'customer_name_snapshot_v2', 'vehicle_plate_snapshot_v2',
  ]);

  const reportLabelOverrides: Record<string, string> = {
    franchisee_company: 'Franqueado',
    franchisee_code: 'Código',
    payment_method: 'Pagamento',
    payment_amount: 'Valor',
    payment_status: 'Situação',
    payment_due_date: 'Data',
  };

  const printFields = useMemo(() => {
    const mainFields = reportFields.filter(
      (field) => !printExcludedFields.has(field) && !paymentReportFields.includes(field)
    );
    return [...mainFields, ...paymentReportFields];
  }, [reportFields]);

  function reportLabel(field: string) {
    return reportLabelOverrides[field] || formatFieldLabel(field);
  }

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      if (filterFranchisee !== 'all' && order.franchisee_id !== filterFranchisee) return false;
      return true;
    });
  }, [orders, filterFranchisee]);

  const visibleOrders = filteredOrders.slice(0, visibleCount);

  useEffect(() => {
    ordersPageViewState = { filterStatus, filterFranchisee, visibleCount };
  }, [filterStatus, filterFranchisee, visibleCount]);

  useEffect(() => {
    setVisibleCount(10);
  }, [filterStatus, filterFranchisee]);

  function getReportValue(order: any, field: string) {
    if (field === 'franchisee_company') return order.franchisee?.company_name || '—';
    if (field === 'franchisee_code') return order.franchisee?.code || '—';
    if (field === 'payment_method') return order.payment?.payment_method || '—';
    if (field === 'payment_amount') {
      return order.payment?.amount != null ? formatCurrency(Number(order.payment.amount)) : '—';
    }
    if (field === 'payment_status') {
      return paymentStatusOptions.find((o) => o.value === order.payment?.payment_status)?.label || '—';
    }
    if (field === 'payment_due_date') {
      return order.payment?.due_date ? formatDate(order.payment.due_date) : '—';
    }
    if (field === 'created_at') return order.created_at ? formatDate(order.created_at) : '—';
    return order[field];
  }

  function exportReportCSV() {
    if (filteredOrders.length === 0) return;
    const header = reportFields.map((field) => csvValue(formatFieldLabel(field))).join(',');
    const rows = filteredOrders.map((order) =>
      reportFields.map((field) => csvValue(getReportValue(order, field))).join(',')
    );
    const blob = new Blob(['\uFEFF' + [header, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio-pedidos-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function printReport() {
    if (filteredOrders.length === 0) return;
    const selectedFranchisee = franchisees.find((f) => f.id === filterFranchisee);
    const rows = filteredOrders.map((order) => `
      <tr>
        ${printFields.map((field) => `<td>${htmlValue(getReportValue(order, field))}</td>`).join('')}
      </tr>
    `).join('');

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Relatorio de Pedidos</title>
          <style>
            body { font-family: Arial, sans-serif; color: #111; margin: 24px; }
            h1 { font-size: 20px; margin: 0 0 6px; }
            p { margin: 0 0 14px; color: #555; font-size: 12px; }
            table { width: 100%; border-collapse: collapse; font-size: 10px; }
            th, td { border: 1px solid #ddd; padding: 6px; text-align: left; vertical-align: top; white-space: pre-wrap; }
            th { background: #f3f4f6; font-weight: 700; }
            @media print { body { margin: 12mm; } }
          </style>
        </head>
        <body>
          <h1>Relatorio de Pedidos</h1>
          <p>${filteredOrders.length} pedido(s) | Franqueado: ${htmlValue(selectedFranchisee?.company_name || 'Todos')} | Gerado em ${htmlValue(formatDate(new Date().toISOString()))}</p>
          <table>
            <thead>
              <tr>${printFields.map((field) => `<th>${htmlValue(reportLabel(field))}</th>`).join('')}</tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }

  const paymentStatusOptions = [
    { value: 'pendente', label: 'Aberto' },
    { value: 'pago', label: 'Pago' },
    { value: 'vencido', label: 'Vencido' },
    { value: 'cancelado', label: 'Cancelado' },
  ] as const;

  function updateStatusFilter(nextStatus: string) {
    setFilterStatus(nextStatus);
    const nextParams = new URLSearchParams(searchParams);
    if (nextStatus === 'all') {
      nextParams.delete('status');
    } else {
      nextParams.set('status', nextStatus);
    }
    setSearchParams(nextParams, { replace: true });
  }

  function openOrderDetails(order: any) {
    setOrderFiles(order.order_files || []);
    setSelectedOrder(order);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('orderId', order.id);
    setSearchParams(nextParams, { replace: true });
  }

  function closeOrderDetails() {
    setSelectedOrder(null);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('orderId');
    setSearchParams(nextParams, { replace: true });
  }

  function openPaymentModal(order: any) {
  const dueDate = order?.due_date || (order?.created_at ? new Date(order.created_at).toISOString().slice(0, 10) : '');
  setPaymentForm({
    amount:
      typeof order?.amount_paid === 'number'
        ? order.amount_paid.toFixed(2)
        : typeof order?.total_amount === 'number'
        ? order.total_amount.toFixed(2)
        : '0.00',
    paymentMethod: order?.payment_method || 'PIX',
    dueDate,
    paymentStatus: order?.payment_status || 'pendente',
  });
  setPaymentModalOrder(order);
  loadExistingPayment(order.id);
}

async function loadExistingPayment(orderId: string) {
  try {
    const { data, error } = await supabase
      .from('financial_records')
      .select('id, amount, payment_method, payment_status, due_date')
      .eq('order_id', orderId)
      .eq('type', 'payment')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.warn('Erro ao buscar pagamento existente:', error);
      return;
    }

    if (data) {
      setPaymentForm({
        amount: data.amount != null ? Number(data.amount).toFixed(2) : '0.00',
        paymentMethod: data.payment_method || 'PIX',
        dueDate: data.due_date || '',
        paymentStatus: data.payment_status || 'pendente',
      });
    }
  } catch (err) {
    console.error('Erro ao carregar pagamento existente:', err);
  }
}

 async function savePayment() {
  if (!paymentModalOrder) return;

  if (!isAdmin || !user?.id) {
    setError('Sua sessão não tem permissão de administrador. Saia e entre novamente.');
    return;
  }

  if (!paymentModalOrder.id || paymentModalOrder.id.trim() === '') {
    setError('Erro: ID do pedido inválido. Recarregue e tente novamente.');
    return;
  }

  if (!paymentForm.amount || Number(paymentForm.amount) <= 0) {
    setError('Por favor, insira um valor maior que zero.');
    return;
  }

  setSavingPayment(true);
  try {
    const paidAt = paymentForm.paymentStatus === 'pago' ? new Date().toISOString() : null;

    const payload: any = {
      order_id: paymentModalOrder.id,
      type: 'payment',
      amount: Number(paymentForm.amount),
      description: `Pagamento pedido ${paymentModalOrder.order_number || paymentModalOrder.id}`,
      payment_status: paymentForm.paymentStatus || 'pendente',
      payment_method: paymentForm.paymentMethod || 'PIX',
      due_date: paymentForm.dueDate?.trim() ? paymentForm.dueDate : null,
      paid_at: paidAt,
      created_by: user.id,
    };

    if (paymentModalOrder.franchisee_id?.trim()) {
      payload.franchisee_id = paymentModalOrder.franchisee_id;
    }

    const { data: existingPayment, error: existingPaymentError } = await supabase
      .from('financial_records')
      .select('id')
      .eq('order_id', paymentModalOrder.id)
      .eq('type', 'payment')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (existingPaymentError) throw existingPaymentError;

    if (existingPayment?.id) {
      const { error: updatePaymentError } = await supabase
        .from('financial_records')
        .update(payload)
        .eq('id', existingPayment.id);

      if (updatePaymentError) throw updatePaymentError;
    } else {
      const { error: insertPaymentError } = await supabase
        .from('financial_records')
        .insert([payload]);

      if (insertPaymentError) throw insertPaymentError;
    }

    const { error: updateOrderError } = await supabase
      .from('orders')
      .update({
        payment_method: payload.payment_method,
        payment_status: payload.payment_status,
        due_date: payload.due_date,
        paid_at: payload.paid_at,
        amount_paid: payload.amount,
        updated_at: new Date().toISOString(),
      })
      .eq('id', paymentModalOrder.id);

    if (updateOrderError) throw updateOrderError;

    await loadOrders();
    setPaymentModalOrder(null);
    setPaymentForm({ amount: '0.00', paymentMethod: 'PIX', dueDate: '', paymentStatus: 'pendente' });
    setError('');
  } catch (err) {
    console.error('Erro ao salvar pagamento:', err);
    setError(`Falha ao salvar pagamento: ${err instanceof Error ? err.message : 'Tente novamente.'}`);
  } finally {
    setSavingPayment(false);
  }
}

  async function loadOrderFiles(orderId: string) {
    setLoadingFiles(true);
    setFilesLoadError('');
    try {
      const { data, error } = await supabase
        .from('order_files')
        .select('*')
        .eq('order_id', orderId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      const files = data || [];
      setOrderFiles(files);
      setOrders((currentOrders) =>
        currentOrders.map((order) =>
          order.id === orderId ? { ...order, order_files: files } : order
        )
      );
      setSelectedOrder((currentOrder: any) =>
        currentOrder?.id === orderId ? { ...currentOrder, order_files: files } : currentOrder
      );
    } catch (err) {
      console.error('Erro ao carregar arquivos:', err);
      setFilesLoadError('Não foi possível carregar os arquivos do pedido.');
      setOrderFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }

  async function downloadOrderFile(file: any) {
    setFileError('');
    try {
      const url = await storage.getSignedUrl(file.file_path);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Erro ao gerar link de download:', err);
      setFileError('Não foi possível gerar o link de download.');
    }
  }

  async function deleteOrderFile(file: any) {
    const confirmed = window.confirm(`Excluir o arquivo "${file.file_name}"?`);
    if (!confirmed) return;

    setDeletingFileId(file.id);
    setFileError('');
    try {
      const { error } = await supabase
        .from('order_files')
        .delete()
        .eq('id', file.id);

      if (error) throw error;

      try {
        await storage.deleteFile(file.file_path);
      } catch (storageError) {
        console.warn('Registro removido, mas não foi possível excluir o arquivo do Storage:', storageError);
        setFileError('Arquivo removido da lista, mas não foi possível apagar o arquivo do Storage.');
      }

      setOrderFiles((currentFiles) => currentFiles.filter((currentFile) => currentFile.id !== file.id));
      setOrders((currentOrders) =>
        currentOrders.map((order) =>
          order.id === file.order_id
            ? { ...order, order_files: (order.order_files || []).filter((currentFile: any) => currentFile.id !== file.id) }
            : order
        )
      );
      setSelectedOrder((currentOrder: any) =>
        currentOrder?.id === file.order_id
          ? { ...currentOrder, order_files: (currentOrder.order_files || []).filter((currentFile: any) => currentFile.id !== file.id) }
          : currentOrder
      );
    } catch (err) {
      console.error('Erro ao excluir arquivo:', err);
      setFileError(`Falha ao excluir arquivo: ${err instanceof Error ? err.message : 'tente novamente.'}`);
    } finally {
      setDeletingFileId('');
    }
  }

  async function handleAdminUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const fileList = Array.from(event.target.files || []);
    if (fileList.length === 0 || !selectedOrder) return;
    if (!user?.id) {
      setFileError('Sessão expirada. Faça login novamente para enviar arquivos.');
      event.target.value = '';
      return;
    }

    setUploadingFiles(true);
    setFileError('');
    try {
      for (const file of fileList) {
        const path = await storage.uploadOrderFile(selectedOrder.id, file);
        const { error } = await supabase.from('order_files').insert({
          order_id: selectedOrder.id,
          file_name: file.name,
          file_path: path,
          file_size: file.size,
          mime_type: file.type,
          uploaded_by: user.id,
          sender_role: 'admin',
        });
        if (error) {
          await storage.deleteFile(path);
          throw error;
        }
      }
      await loadOrderFiles(selectedOrder.id);
    } catch (err) {
      console.error('Erro ao enviar arquivo:', err);
      setFileError(`Falha ao enviar arquivo: ${err instanceof Error ? err.message : 'tente novamente.'}`);
    } finally {
      setUploadingFiles(false);
      event.target.value = '';
    }
  }

  // ─── Componente de lista de arquivos reutilizável ─────────────────────────
  // Usado tanto no card do pedido (inline, sempre visível) quanto no modal de detalhes.
  function FileList({
    files,
    loading: filesLoading,
    error: filesError,
    compact = false,
  }: {
    files: any[];
    loading: boolean;
    error: string;
    compact?: boolean;
  }) {
    if (filesLoading) {
      return (
        <div style={{ fontSize: 12, color: colors.textSecondary, padding: compact ? '4px 0' : 8 }}>
          Carregando arquivos...
        </div>
      );
    }

    if (filesError) {
      return (
        <div style={{ fontSize: 12, color: '#c0392b', padding: compact ? '4px 0' : 8 }}>
          {filesError}
        </div>
      );
    }

    if (files.length === 0) {
      return (
        <div style={{ fontSize: 12, color: colors.textSecondary, padding: compact ? '4px 0' : 8 }}>
          Nenhum arquivo ainda.
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 6 : 8 }}>
        {files.map((file) => {
          // Usa sender_role salvo no upload — confiável para admin e franqueado
          const isFromAdmin = file.sender_role === 'admin';

          return (
            <div
              key={file.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
                padding: compact ? '8px 10px' : '12px 14px',
                borderRadius: 8,
                border: `1px solid ${isFromAdmin ? '#03160a' : colors.border}`,
                background: isFromAdmin
                  ? (isDark ? '#141414' : '#f0fdf4')
                  : (isDark ? '#181818' : '#fafafa'),
              }}
            >
              {/* Info do arquivo */}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: compact ? 14 : 16 }}>
                    {isFromAdmin ? '📤' : '📥'}
                  </span>
                  <span
                    style={{
                      fontSize: compact ? 12 : 13,
                      color: colors.text,
                      fontWeight: 600,
                      wordBreak: 'break-word',
                    }}
                  >
                    {file.file_name}
                  </span>
                  <span
                    style={{
                      fontSize: 9,
                      padding: '2px 7px',
                      borderRadius: 999,
                      background: isFromAdmin ? '#88888825' : colors.background,
                      border: `1px solid ${isFromAdmin ? '#2e2e2e' : colors.border}`,
                      color: isFromAdmin ? '#181818' : colors.textSecondary,
                      whiteSpace: 'nowrap',
                      fontWeight: 700,
                    }}
                  >
                    {isFromAdmin ? 'REMAP' : 'ORIGINAL'}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: colors.textSecondary,
                    marginTop: 3,
                    paddingLeft: 22,
                  }}
                >
                  {file.file_size ? `${(file.file_size / 1024).toFixed(1)} KB` : ''}
                  {file.file_size && file.created_at ? ' · ' : ''}
                  {file.created_at ? formatDate(file.created_at) : ''}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadOrderFile(file);
                  }}
                  title={`Baixar ${file.file_name}`}
                  style={{
                    padding: compact ? '6px 10px' : '8px 14px',
                    borderRadius: 8,
                    border: 'none',
                    background: isFromAdmin ? '#646464' : (isDark ? '#2a2a2a' : '#e5e7eb'),
                    color: isFromAdmin ? '#052e16' : colors.text,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  ⬇ Baixar
                </button>
                <button
                  type="button"
                  disabled={deletingFileId === file.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteOrderFile(file);
                  }}
                  title={`Excluir ${file.file_name}`}
                  style={{
                    padding: compact ? '6px 10px' : '8px 14px',
                    borderRadius: 8,
                    border: `1px solid ${isDark ? '#4a1f1f' : '#fecaca'}`,
                    background: deletingFileId === file.id ? (isDark ? '#222' : '#eee') : (isDark ? '#2a1212' : '#fef2f2'),
                    color: isDark ? '#fca5a5' : '#b91c1c',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: deletingFileId === file.id ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                    opacity: deletingFileId === file.id ? 0.7 : 1,
                  }}
                >
                  {deletingFileId === file.id ? 'Excluindo...' : 'Excluir'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ─── Componente de arquivos inline no card do pedido ──────────────────────
  // Carrega e exibe os arquivos diretamente na listagem, sem precisar abrir modal.
  function OrderCardFiles({ orderId }: { orderId: string }) {
    const [cardFiles, setCardFiles] = useState<any[]>([]);
    const [cardLoading, setCardLoading] = useState(true);
    const [cardError, setCardError] = useState('');

    useEffect(() => {
      let cancelled = false;
      async function fetch() {
        setCardLoading(true);
        setCardError('');
        try {
          const { data, error } = await supabase
            .from('order_files')
            .select('*')
            .eq('order_id', orderId)
            .order('created_at', { ascending: false });
          if (error) throw error;
          if (!cancelled) setCardFiles(data || []);
        } catch {
          if (!cancelled) setCardError('Não foi possível carregar os arquivos.');
        } finally {
          if (!cancelled) setCardLoading(false);
        }
      }
      fetch();
      return () => { cancelled = true; };
    }, [orderId]);

    if (cardLoading) {
      return (
        <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 10 }}>
          Carregando arquivos...
        </div>
      );
    }

    if (cardError) {
      return (
        <div style={{ fontSize: 11, color: '#c0392b', marginTop: 10 }}>{cardError}</div>
      );
    }

    if (cardFiles.length === 0) return null;

    return (
      <div style={{ marginTop: 12 }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: colors.textSecondary,
            letterSpacing: 0.5,
            marginBottom: 6,
            textTransform: 'uppercase',
          }}
        >
       
        </div>
        <FileList files={cardFiles} loading={false} error="" compact />
      </div>
    );
  }



// ...existing code...
// REMOVER este bloco da posição atual (antes dos useEffect):
// if (!isAdmin) {
//   return (
//     <div style={{ padding: 60, textAlign: 'center' }}>
//       <h2>Acesso negado</h2>
//     </div>
//   );
// }

  async function deleteOrder(order: any) {
    const confirmed = window.confirm(
      `Excluir o pedido ${order.order_number || ''}? Esta ação é permanente e também removerá os pagamentos e arquivos vinculados.`
    );
    if (!confirmed) return;

    setDeletingOrderId(order.id);
    setError('');
    try {
      const { error: financialError } = await supabase
        .from('financial_records')
        .delete()
        .eq('order_id', order.id);
      if (financialError) throw financialError;

      const { error: orderError } = await supabase
        .from('orders')
        .delete()
        .eq('id', order.id);
      if (orderError) throw orderError;

      await Promise.all(
        (order.order_files || []).map((file: any) =>
          storage.deleteFile(file.file_path).catch(() => undefined)
        )
      );

      setOrders((current) => current.filter((currentOrder) => currentOrder.id !== order.id));
      ordersPageCache.forEach((cached, key) => {
        ordersPageCache.set(key, {
          ...cached,
          orders: cached.orders.filter((cachedOrder) => cachedOrder.id !== order.id),
        });
      });
      if (selectedOrder?.id === order.id) closeOrderDetails();
    } catch (err) {
      console.error('Erro ao excluir pedido:', err);
      setError(err instanceof Error ? err.message : 'Não foi possível excluir o pedido.');
    } finally {
      setDeletingOrderId('');
    }
  }

useEffect(() => {
  const cached = ordersPageCache.get(filterStatus);
  if (cached) {
    setOrders(cached.orders);
    setFranchisees(cached.franchisees);
    setLoading(false);
    return;
  }
  loadOrders(false);
}, [filterStatus]);

useEffect(() => {
  const statusParam = searchParams.get('status');
  if (!statusParam) return;
  const validStatuses = new Set<string>(['all', ...statusOptions.map((option) => option.value)]);
  if (!validStatuses.has(statusParam)) return;
  if (statusParam !== filterStatus) setFilterStatus(statusParam);
}, [searchParams, filterStatus]);

useEffect(() => {
  if (selectedOrder?.id) {
    loadOrderFiles(selectedOrder.id);
  } else {
    setOrderFiles([]);
    setFileError('');
    setFilesLoadError('');
  }
}, [selectedOrder?.id]);

useEffect(() => {
  const orderIdFromQuery = searchParams.get('orderId');
  if (!orderIdFromQuery) return;

  const matchedOrder = orders.find((order) => order.id === orderIdFromQuery);
  if (matchedOrder) {
    setOrderFiles(matchedOrder.order_files || []);
    setSelectedOrder(matchedOrder);
    return;
  }

  if (!loading) {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('orderId');
    setSearchParams(nextParams, { replace: true });
  }
}, [searchParams, setSearchParams, orders, loading]);

// ADICIONAR aqui (depois dos hooks):
if (!isAdmin) {
  return (
    <div style={{ padding: 60, textAlign: 'center' }}>
      <h2>Acesso negado</h2>
    </div>
  );
}
// ...existing code...

 async function loadOrders(forceRefresh = true) {
  if (!forceRefresh) {
    const cached = ordersPageCache.get(filterStatus);
    if (cached) {
      setOrders(cached.orders);
      setFranchisees(cached.franchisees);
      setLoading(false);
      return;
    }
  }
  setLoading(true);
  setError('');

  try {
    let query = supabase
      .from('orders')
      .select('*')
      .order('created_at', { ascending: false });

    if (filterStatus !== 'all') {
      query = query.eq('status', filterStatus);
    }

    const [
      { data: ordersData, error: ordersError },
      { data: allFranchisees, error: franchiseesError },
    ] = await Promise.all([
      query,
      supabase
        .from('franchisees')
        .select('id, company_name, code, user_id')
        .order('company_name'),
    ]);

    if (ordersError) throw ordersError;
    if (franchiseesError) throw franchiseesError;

    setFranchisees(allFranchisees || []);

    const franchiseeMap = new Map();

    (allFranchisees || []).forEach((f) => {
      franchiseeMap.set(String(f.id), f);
    });

    const orderIds = (ordersData || [])
      .map((o) => String(o.id))
      .filter(Boolean);

    let payments: any[] = [];
    let files: any[] = [];

if (orderIds.length > 0) {
  const [
    { data: paymentsData, error: paymentsError },
    { data: filesData, error: filesError },
  ] = await Promise.all([
    supabase
      .from('financial_records')
      .select('*')
      .eq('type', 'payment')
      .in('order_id', orderIds)
      .order('created_at', { ascending: false }),

    supabase
      .from('order_files')
      .select('*')
      .in('order_id', orderIds)
      .order('created_at', { ascending: false }),
  ]);

  if (paymentsError) console.error('Erro payments:', paymentsError);
  if (filesError) console.error('Erro files:', filesError);

  payments = paymentsData || [];
  files = filesData || [];
}

const paymentMap = new Map<string, any>();
payments.forEach((payment) => {
  const key = String(payment.order_id);
  if (!paymentMap.has(key)) paymentMap.set(key, payment); // já está desc, pega o mais recente
});

    const filesByOrder: Record<string, any[]> = {};

    files.forEach((file) => {
      const key = String(file.order_id);

      if (!filesByOrder[key]) {
        filesByOrder[key] = [];
      }

      filesByOrder[key].push(file);
    });

    console.log('=== FILES BY ORDER ===');
    console.log(filesByOrder);

    const ordersWithRelations = (ordersData || []).map((order) => {
      const orderId = String(order.id);

      return {
        ...order,
        flow_status: mapStatusForFlow(order.status),

        franchisee:
          franchiseeMap.get(String(order.franchisee_id)) || {
            company_name: '—',
            code: '—',
          },

        payment: paymentMap.get(orderId) || null,

        order_files: filesByOrder[orderId] || [],
      };
    });

    console.log('=== ORDERS FINAL ===');
    console.log(
      ordersWithRelations.map((o) => ({
        pedido: o.order_number,
        id: o.id,
        arquivos: o.order_files?.length || 0,
      }))
    );

    setOrders(ordersWithRelations);
    ordersPageCache.set(filterStatus, {
      orders: ordersWithRelations,
      franchisees: allFranchisees || [],
    });
  } catch (err) {
    console.error(err);
    setError('Erro ao carregar pedidos. Tente novamente.');
  } finally {
    setLoading(false);
  }
}

  async function updateOrderStatus(orderId: string, newStatus: OrderStatus) {
    setUpdating(true);
    try {
      const { error } = await supabase
        .from('orders')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', orderId);

      if (error) throw error;
      await loadOrders();
      closeOrderDetails();
    } catch (error) {
      console.error('Erro ao atualizar:', error);
    } finally {
      setUpdating(false);
    }
  }

  if (loading) {
    return (
      <div style={{ background: colors.background, minHeight: '100vh', padding: 24, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 16, color: colors.text }}>Carregando pedidos...</div>
          <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 8 }}>Aguarde um momento</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: colors.background, minHeight: '100vh', padding: 24 }}>
        <div style={{ textAlign: 'center', padding: 60, background: colors.surface, borderRadius: 12 }}>
          <h3 style={{ color: '#c8c8c8' }}>Erro</h3>
          <p style={{ color: colors.textSecondary }}>{error}</p>
          <button
            onClick={() => loadOrders()}
            style={{ padding: '8px 16px', background: colors.accent, border: 'none', borderRadius: 6, cursor: 'pointer' }}
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-page" style={{ background: colors.background, minHeight: '100vh', padding: 24, overflowX: 'hidden' }}>
      <style>{`
        .orders-page, .orders-page * { box-sizing: border-box; }
        .orders-page > div { width: 100%; min-width: 0; }
        .orders-list { width: 100%; min-width: 0; }
        .orders-card { width: 100%; min-width: 0; overflow: hidden; }
        .orders-card-header { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 12px; }
        .orders-card-header > * { min-width: 0; max-width: 100%; }
        .orders-card-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
        .orders-card-actions > * { max-width: 100%; }
        .orders-fields-grid { width: 100%; min-width: 0; display: grid; grid-template-columns: repeat(auto-fit, minmax(min(170px, 100%), 1fr)); gap: 8px; margin-top: 16px; }
        .orders-field-value { min-width: 0; max-width: 100%; overflow-wrap: anywhere; word-break: break-word; white-space: pre-wrap; }
        .orders-filters > * { max-width: 100%; }
        @media (max-width: 720px) {
          .orders-page { padding: 12px !important; }
          .orders-filters select { width: 100%; min-width: 0 !important; }
          .orders-card-header { flex-direction: column; }
          .orders-card-actions { width: 100%; display: grid; grid-template-columns: 1fr 1fr; }
          .orders-card-actions select, .orders-card-actions button { width: 100%; min-height: 38px; }
          .orders-fields-grid { grid-template-columns: 1fr 1fr; }
        }
        @media (max-width: 430px) {
          .orders-card-actions, .orders-fields-grid { grid-template-columns: 1fr; }
        }
      `}</style>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>

        <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ color: colors.text, fontSize: 24, margin: 0 }}>Gerenciar Pedidos</h1>
            <p style={{ color: colors.textSecondary, marginTop: 4 }}>Visualize, crie e atualize o status dos pedidos</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/admin/orders/new')}
            style={{
              padding: '10px 16px',
              borderRadius: 8,
              border: 'none',
              background: colors.accent,
              color: '#000',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 800,
            }}
          >
            + Novo pedido
          </button>
        </div>

        {/* Filtros */}
        <div className="orders-filters" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24, alignItems: 'center', minWidth: 0 }}>
          <button
            onClick={() => updateStatusFilter('all')}
            style={{
              padding: '6px 16px',
              borderRadius: 20,
              border: `1px solid ${filterStatus === 'all' ? colors.accent : colors.border}`,
              background: filterStatus === 'all' ? colors.accent : 'transparent',
              color: filterStatus === 'all' ? '#000' : colors.textSecondary,
              cursor: 'pointer',
              fontSize: 12,
            }}
          >
            Todos ({orders.length})
          </button>
          {statusOptions.map(opt => (
            <button
              key={opt.value}
              onClick={() => updateStatusFilter(opt.value)}
              style={{
                padding: '6px 16px',
                borderRadius: 20,
                border: `1px solid ${filterStatus === opt.value ? opt.color : colors.border}`,
                background: filterStatus === opt.value ? opt.color : 'transparent',
                color: filterStatus === opt.value ? '#fff' : colors.textSecondary,
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              {opt.label}
            </button>
          ))}

          <select
            value={filterFranchisee}
            onChange={(e) => setFilterFranchisee(e.target.value)}
            style={{
              padding: '7px 12px',
              borderRadius: 8,
              border: `1px solid ${colors.border}`,
              background: colors.surface,
              color: colors.text,
              fontSize: 12,
              minWidth: 220,
            }}
          >
            <option value="all">Todos os franqueados</option>
            {franchisees.map((franchisee) => (
              <option key={franchisee.id} value={franchisee.id}>
                {franchisee.company_name} ({franchisee.code})
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={exportReportCSV}
            disabled={filteredOrders.length === 0}
            style={{
              padding: '7px 12px',
              borderRadius: 8,
              border: `1px solid ${colors.border}`,
              background: isDark ? '#1a1a1a' : '#fafafa',
              color: colors.text,
              cursor: filteredOrders.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: 12,
              fontWeight: 700,
              opacity: filteredOrders.length === 0 ? 0.5 : 1,
            }}
          >
            Exportar relatório
          </button>

          <button
            type="button"
            onClick={printReport}
            disabled={filteredOrders.length === 0}
            style={{
              padding: '7px 12px',
              borderRadius: 8,
              border: `1px solid ${colors.border}`,
              background: isDark ? '#1a1a1a' : '#fafafa',
              color: colors.text,
              cursor: filteredOrders.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: 12,
              fontWeight: 700,
              opacity: filteredOrders.length === 0 ? 0.5 : 1,
            }}
          >
            Imprimir
          </button>
        </div>

        {/* Lista de Pedidos */}
        {filteredOrders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, background: colors.surface, borderRadius: 12 }}>
            <p style={{ color: colors.textSecondary }}>Nenhum pedido encontrado</p>
          </div>
        ) : (
          <div className="orders-list" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {visibleOrders.map((order) => (
              <div
                key={order.id}
                className="orders-card"
                style={{
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                  borderLeft: `4px solid ${statusOptions.find((s) => s.value === order.flow_status)?.color || colors.accent}`,
                  borderRadius: 14,
                  padding: 18,
                  transition: 'all 0.2s ease',
                  cursor: 'default',
                  boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.25)' : '0 4px 14px rgba(0,0,0,0.06)',
                }}
              >
                {/* Cabeçalho do card */}
                <div className="orders-card-header">
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: colors.accent, letterSpacing: 0.4 }}>
                      {order.order_number}
                    </div>
                    <div style={{ fontSize: 14, color: colors.text, marginTop: 4, fontWeight: 600 }}>
                      {order.franchisee?.company_name || '—'}
                    </div>
                    <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
                      Placa: {order.vehicle_plate || '—'} | Data: {formatDate(order.created_at)}
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 10, padding: '4px 8px', borderRadius: 999, border: `1px solid ${colors.border}`, color: colors.textSecondary }}>
                        Código: {order.franchisee?.code || '—'}
                      </span>
                      <span style={{ fontSize: 10, padding: '4px 8px', borderRadius: 999, border: `1px solid ${colors.border}`, color: colors.textSecondary }}>
                        ID: {order.id?.slice(0, 8)}...
                      </span>
                    </div>
                  </div>

                  <div className="orders-card-actions">
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      borderRadius: 20,
                      fontSize: 11,
                      fontWeight: 600,
                      background: (statusOptions.find(s => s.value === order.flow_status)?.color || '#ccc') + '20',
                      color: statusOptions.find(s => s.value === order.flow_status)?.color,
                    }}>
                      {statusOptions.find(s => s.value === order.flow_status)?.label || order.flow_status}
                    </span>

                    <select
                      value={order.flow_status}
                      onChange={(e) => updateOrderStatus(order.id, e.target.value as OrderStatus)}
                      disabled={updating}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 6,
                        border: `1px solid ${colors.border}`,
                        background: colors.surface,
                        color: colors.text,
                        fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      {statusOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>

                    <button
                      type="button"
                      onClick={() => openPaymentModal(order)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 6,
                        border: `1px solid ${colors.border}`,
                        background: isDark ? '#1a1a1a' : '#fafafa',
                        color: colors.text,
                        fontSize: 12,
                        cursor: 'pointer',
                        fontWeight: 600,
                      }}
                    >
                      Pagamento
                    </button>

                    <button
                      type="button"
                      onClick={() => openOrderDetails(order)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 6,
                        border: `1px solid ${colors.border}`,
                        background: isDark ? '#1a1a1a' : '#fafafa',
                        color: colors.text,
                        fontSize: 12,
                        cursor: 'pointer',
                        fontWeight: 600,
                      }}
                    >
                      Ver detalhes
                    </button>

                    <button
                      type="button"
                      onClick={() => deleteOrder(order)}
                      disabled={deletingOrderId === order.id}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 6,
                        border: `1px solid ${isDark ? '#6b2a2a' : '#efb4b4'}`,
                        background: isDark ? '#2a1212' : '#fef2f2',
                        color: isDark ? '#f0a0a0' : '#b42318',
                        fontSize: 12,
                        cursor: deletingOrderId === order.id ? 'wait' : 'pointer',
                        fontWeight: 700,
                        opacity: deletingOrderId === order.id ? 0.65 : 1,
                      }}
                    >
                      {deletingOrderId === order.id ? 'Excluindo...' : 'Excluir pedido'}
                    </button>
                  </div>
                </div>

                {order.model && (
                  <div style={{ marginTop: 8, fontSize: 11, color: colors.textSecondary, overflowWrap: 'anywhere' }}>
                    Modelo: {order.model}
                  </div>
                )}

                {order.notes && (
                  <div style={{ marginTop: 8, fontSize: 11, color: colors.textSecondary, background: colors.background, padding: 8, borderRadius: 6, overflowWrap: 'anywhere' }}>
                    <span style={{ fontWeight: 700 }}>Obs: </span>
                    <ExpandableFieldValue value={String(order.notes)} color={colors.textSecondary} buttonColor={colors.accent} />
                  </div>
                )}

                {/* ── Arquivos sempre visíveis no card ── */}
                <div style={{ marginTop: 12 }}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: colors.textSecondary,
                      letterSpacing: 0.5,
                      marginBottom: 6,
                      textTransform: 'uppercase',
                    }}
                  >
                    Arquivos ({order.order_files?.length || 0})
                  </div>
                 <OrderCardFiles orderId={order.id} />
                </div>
              </div>
            ))}
            {visibleCount < filteredOrders.length && (
              <button type="button" onClick={() => setVisibleCount((count) => count + 10)}
                style={{ alignSelf: 'center', padding: '9px 20px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.surface, color: colors.text, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                Ver mais ({filteredOrders.length - visibleCount} restantes)
              </button>
            )}
          </div>
        )}

        {/* ── Modal de Pagamento ── */}
        {paymentModalOrder && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.72)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 18,
              zIndex: 1100,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 'min(480px, 96vw)',
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: 14,
                boxShadow: '0 18px 48px rgba(0,0,0,0.35)',
                overflow: 'hidden',
              }}
            >
              <div style={{ padding: '16px 16px 8px', borderBottom: `1px solid ${colors.border}` }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>Pagamento do pedido</div>
                <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 4 }}>
                  {paymentModalOrder.order_number || 'Pedido'} · {paymentModalOrder.franchisee?.company_name || '—'}
                </div>
              </div>

              <div style={{ display: 'grid', gap: 12, padding: 16 }}>
                <label style={{ display: 'grid', gap: 6, color: colors.text, fontSize: 12 }}>
                  Valor R$
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm((prev) => ({ ...prev, amount: e.target.value }))}
                    style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${colors.border}`, background: isDark ? '#181818' : '#fff', color: colors.text }}
                  />
                </label>

                <label style={{ display: 'grid', gap: 6, color: colors.text, fontSize: 12 }}>
                  Forma de pagamento
                  <input
                    type="text"
                    value={paymentForm.paymentMethod}
                    onChange={(e) => setPaymentForm((prev) => ({ ...prev, paymentMethod: e.target.value }))}
                    placeholder="PIX, boleto, cartão..."
                    style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${colors.border}`, background: isDark ? '#181818' : '#fff', color: colors.text }}
                  />
                </label>

                <label style={{ display: 'grid', gap: 6, color: colors.text, fontSize: 12 }}>
                  Vencimento
                  <input
                    type="date"
                    value={paymentForm.dueDate}
                    onChange={(e) => setPaymentForm((prev) => ({ ...prev, dueDate: e.target.value }))}
                    style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${colors.border}`, background: isDark ? '#181818' : '#fff', color: colors.text }}
                  />
                </label>

                <label style={{ display: 'grid', gap: 6, color: colors.text, fontSize: 12 }}>
                  Situação de pagamento
                  <select
                    value={paymentForm.paymentStatus}
                    onChange={(e) => setPaymentForm((prev) => ({ ...prev, paymentStatus: e.target.value }))}
                    style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${colors.border}`, background: isDark ? '#181818' : '#fff', color: colors.text }}
                  >
                    {paymentStatusOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>

                <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 8 }}>
                  <div style={{ fontSize: 12, color: colors.textSecondary }}>
                    Valor exibido: {formatCurrency(Number(paymentForm.amount || 0))}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => savePayment()}
                      disabled={savingPayment}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 8,
                        border: `1px solid ${colors.border}`,
                        background: isDark ? '#262e28' : '#081f17',
                        color: '#fff',
                        cursor: savingPayment ? 'not-allowed' : 'pointer',
                        fontWeight: 700,
                      }}
                    >
                      {savingPayment ? 'Salvando...' : 'Salvar'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentModalOrder(null)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 8,
                        border: `1px solid ${colors.border}`,
                        background: isDark ? '#1a1a1a' : '#fafafa',
                        color: colors.text,
                        cursor: 'pointer',
                        fontWeight: 600,
                      }}
                    >
                      Fechar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Modal de Detalhes do Pedido ── */}
        {selectedOrder && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.72)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 18,
              zIndex: 1000,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 'min(960px, 96vw)',
                maxHeight: '92vh',
                overflow: 'auto',
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: 14,
                boxShadow: '0 18px 48px rgba(0,0,0,0.35)',
              }}
            >
              {/* Header do modal */}
              <div
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  background: colors.surface,
                  borderBottom: `1px solid ${colors.border}`,
                  padding: '14px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>
                    Detalhes do Pedido {selectedOrder.order_number || '—'}
                  </div>
                  <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 2 }}>
                    Franqueado: {selectedOrder.franchisee?.company_name || '—'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closeOrderDetails}
                  style={{
                    border: `1px solid ${colors.border}`,
                    background: 'transparent',
                    color: colors.text,
                    borderRadius: 8,
                    fontSize: 13,
                    cursor: 'pointer',
                    padding: '8px 12px',
                    fontWeight: 600,
                  }}
                >
                  Fechar
                </button>
              </div>

              <div style={{ padding: 16 }}>
                {/* Seção de Arquivos no modal */}
                <div
                  style={{
                    border: `1px solid ${colors.border}`,
                    borderRadius: 12,
                    padding: 16,
                    marginBottom: 16,
                    background: isDark ? '#121212' : '#fafafa',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: colors.text }}>
                      Arquivos do Pedido
                    </div>
                    <label
                      style={{
                        padding: '7px 12px',
                        borderRadius: 8,
                        border: `1px solid ${colors.border}`,
                        background: uploadingFiles ? (isDark ? '#222' : '#eee') : (isDark ? '#1a1a1a' : '#fff'),
                        color: colors.text,
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: uploadingFiles ? 'not-allowed' : 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                      }}
                    >
                      {uploadingFiles ? 'Enviando...' : '📎 Enviar arquivo ao franqueado'}
                      <input
                        type="file"
                        multiple
                        hidden
                        disabled={uploadingFiles}
                        onChange={handleAdminUpload}
                      />
                    </label>
                  </div>

                  {fileError && (
                    <div style={{ fontSize: 12, color: '#c0392b', marginBottom: 10 }}>{fileError}</div>
                  )}

                  <div style={{ marginTop: 10 }}>
                    <FileList
                      files={orderFiles}
                      loading={loadingFiles}
                      error={filesLoadError}
                    />
                  </div>
                </div>

                {/* Campos do pedido */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 10 }}>
                  {[
                    ...detailFieldPriority.filter((field) => field in orderDetails(selectedOrder)),
                    ...Object.keys(orderDetails(selectedOrder)).filter((field) => !detailFieldPriority.includes(field)),
                  ]
                    .filter((field) => field !== 'franchisee' && field !== 'flow_status' && field !== 'payment')
                    .map((field) => (
                      <div
                        key={field}
                        style={{
                          border: `1px solid ${colors.border}`,
                          borderRadius: 10,
                          padding: '10px 12px',
                          background: isDark ? '#121212' : '#fafafa',
                        }}
                      >
                        <div style={{ fontSize: 10, fontWeight: 700, color: colors.textSecondary, letterSpacing: 0.5 }}>
                          {formatFieldLabel(field)}
                        </div>
                        <div style={{ marginTop: 6, fontSize: 12 }}>
                          <ExpandableFieldValue
                            value={formatFieldValue(field, orderDetails(selectedOrder)[field])}
                            color={colors.text}
                            buttonColor={colors.accent}
                          />
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
